import { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Lock, Mail, Loader2 } from 'lucide-react';
import { useBranding } from '@/context/BrandingContext';

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { branding } = useBranding();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Berhasil masuk');
      navigate('/');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal masuk. Periksa email/password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 gradient-hero bg-cover bg-center" style={branding.login_background_url ? { backgroundImage: `linear-gradient(rgba(var(--fpccs-bg-rgb),.78), rgba(var(--fpccs-bg-rgb),.90)), url(${branding.login_background_url})` } : undefined}>
      <div className="w-full max-w-md animate-in-up">
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src={branding.login_logo_url || branding.logo_url || '/fpccs-mark.svg'} alt={branding.company_name} className="w-14 h-14 rounded-xl object-contain" />
          <div className="text-left">
            <div className="text-xl font-semibold tracking-tight">{branding.application_name}</div>
            <div className="text-xs text-subtle-fpccs">{branding.company_name}</div>
          </div>
        </div>

        <div className="card-fpccs p-6 sm:p-8 strip-primary">
          <h1 className="text-2xl font-semibold tracking-tight">Masuk ke Workspace</h1>
          <p className="text-sm text-muted-fpccs mt-1">{branding.tagline || `Workspace ${branding.company_name}`}</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs text-muted-fpccs">Email</Label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fpccs-text-subtle)]" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  data-testid="login-email-input"
                  className="pl-9 bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)] text-[var(--fpccs-text)]"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@perusahaan.com"
                  required
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs text-muted-fpccs">Password</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fpccs-text-subtle)]" />
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  data-testid="login-password-input"
                  className="pl-9 bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)] text-[var(--fpccs-text)]"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-10 bg-[var(--fpccs-primary)] hover:bg-[var(--fpccs-primary-2)] text-[var(--fpccs-bg)] font-medium"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Memproses...</> : 'Masuk'}
            </Button>
          </form>
        </div>

        <div className="text-center text-xs text-subtle-fpccs mt-6"><div>{branding.copyright_text || `© ${new Date().getFullYear()} ${branding.company_name}`}{branding.footer_text ? ` · ${branding.footer_text}` : ''}</div>{branding.website && <a href={branding.website} target="_blank" rel="noreferrer" className="mt-1 inline-block hover:text-[var(--fpccs-primary)]">{branding.website}</a>}</div>
      </div>
    </div>
  );
}
